import React from 'react';
import { Box, Button, Card, FormControl, FormLabel, Input, Option, Select, Stack, Typography, RadioGroup, Radio, Switch, Divider, Slider, Tooltip } from '@mui/joy';
import { DocumentWizardSharedProps, Row, FeeRow } from './DocumentWizardShared';
import { computeTotals } from '../types/documents.types';

interface DepositCardProps extends Pick<DocumentWizardSharedProps, 
  'depositEnabled' | 'setDepositEnabled' | 'depositMode' | 'setDepositMode' | 
  'depositPct' | 'setDepositPct' | 'depositFixed' | 'setDepositFixed' | 'depositAmount' |
  'paymentStructureApplicable' | 'setPaymentStructureApplicable' | 'paymentStructurePattern' | 'setPaymentStructurePattern'
> {
  grandTotal: number;
}

export const DepositCard: React.FC<DepositCardProps> = ({ 
  depositEnabled,
  setDepositEnabled,
  depositMode,
  setDepositMode,
  depositPct,
  setDepositPct,
  depositFixed,
  setDepositFixed,
  depositAmount,
  paymentStructureApplicable,
  setPaymentStructureApplicable,
  paymentStructurePattern,
  setPaymentStructurePattern,
  grandTotal,
}) => {
  const r2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

  const remainingAfterDeposit = Math.max(grandTotal - depositAmount, 0);

  const structures: Array<{ label: string; parts: number[] }> = [
    { label: '20/20/30/30', parts: [20, 20, 30, 30] },
    { label: '25/50/25', parts: [25, 50, 25] },
    { label: '30/30/40', parts: [30, 30, 40] },
    { label: '30/40/30', parts: [30, 40, 30] },
    { label: '40/30/30', parts: [40, 30, 30] },
    { label: '40/40/20', parts: [40, 40, 20] },
    { label: '50/25/25', parts: [50, 25, 25] },
    { label: '50/40/10', parts: [50, 40, 10] },
  ];

  const activeStructure = structures.find(s => s.label === (paymentStructurePattern || '')) || null;

  const handleSelectStructure = (s: { label: string; parts: number[] }) => {
    setPaymentStructureApplicable(true);
    setPaymentStructurePattern(s.label);

    // First integer always represents the deposit percentage
    const depositPctFromStructure = (s.parts[0] ?? 0) / 100;
    setDepositEnabled(true);
    setDepositMode('percent');
    setDepositPct(depositPctFromStructure);
  };

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Typography level="title-md" sx={{ mb: 1 }}>Deposit</Typography>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography level="body-sm">Enable Deposit</Typography>
          <Switch checked={depositEnabled} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDepositEnabled(e.target.checked)} />
        </Stack>
        
        {depositEnabled && (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Typography level="body-sm">Mode:</Typography>
                <RadioGroup
                  orientation="horizontal"
                  value={depositMode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDepositMode(e.target.value as 'percent' | 'fixed')}
                >
                  <Radio value="percent" label="Percentage" />
                  <Radio value="fixed" label="Fixed Amount" />
                </RadioGroup>
              </Stack>
            </Stack>

            {depositMode === 'percent' ? (
              <Stack spacing={1}>
                <Typography level="body-sm">Deposit Percentage</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  {[5, 10, 20, 25, 40, 45, 50, 100].map(pct => (
                    <Button
                      key={pct}
                      size="sm"
                      variant={depositPct === pct / 100 ? 'solid' : 'outlined'}
                      onClick={() => setDepositPct(pct / 100)}
                      sx={{ minWidth: 'auto' }}
                    >
                      {pct}%
                    </Button>
                  ))}
                </Stack>
              </Stack>
            ) : (
              <FormControl>
                <FormLabel>Fixed Deposit Amount</FormLabel>
                <Input
                  type="number"
                  value={String(depositFixed)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDepositFixed(Number(e.target.value || 0))}
                  startDecorator="£"
                />
              </FormControl>
            )}

            <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography level="body-sm" fontWeight="md">Deposit Amount:</Typography>
                <Typography level="title-sm" fontWeight="bold">£{depositAmount.toFixed(2)}</Typography>
              </Stack>
            </Box>
          </>
        )}

        <Divider />

        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography level="body-sm">Payment Structure Applicable?</Typography>
            <Switch
              checked={paymentStructureApplicable}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const enabled = e.target.checked;
                setPaymentStructureApplicable(enabled);
                if (!enabled) {
                  setPaymentStructurePattern(null);
                }
              }}
            />
          </Stack>

          {paymentStructureApplicable && (
            <>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                Choose how the remaining balance is staged after the deposit. This does not change the invoice totals.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
                {structures.map(s => (
                  <Button
                    key={s.label}
                    size="sm"
                    variant={paymentStructurePattern === s.label ? 'solid' : 'outlined'}
                    onClick={() => handleSelectStructure(s)}
                    sx={{ mb: { xs: 0.5, sm: 0 }, minWidth: 0 }}
                  >
                    {s.label}
                  </Button>
                ))}
              </Stack>

              {activeStructure && (
                <Box sx={{ mt: 1, p: 1.25, bgcolor: 'background.level1', borderRadius: 1 }}>
                  <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 'lg' }}>
                    Staged payments (based on Total incl. VAT minus deposit)
                  </Typography>
                  <Stack spacing={0.25}>
                    <Typography level="body-xs">
                      Deposit: {activeStructure.parts[0]}% &rarr; £{r2(grandTotal * (activeStructure.parts[0] / 100)).toFixed(2)}
                    </Typography>
                    {(() => {
                      const remainingParts = activeStructure.parts.slice(1);
                      const sumRemaining = remainingParts.reduce((acc, p) => acc + p, 0) || 1;
                      return remainingParts.map((p, idx) => {
                        const share = p / sumRemaining;
                        const amount = r2(remainingAfterDeposit * share);
                        return (
                          <Typography key={idx} level="body-xs">
                            Stage {idx + 1}: £{amount.toFixed(2)}
                          </Typography>
                        );
                      });
                    })()}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

interface TotalsCardProps extends Pick<DocumentWizardSharedProps, 
  'fees' | 'setFees' | 'vatEnabled' | 'setVatEnabled' | 'vatPct' | 'setVatPct' | 'displayDays' | 'displayHours'
> {
  totals: ReturnType<typeof computeTotals>;
}

export const TotalsCard: React.FC<TotalsCardProps> = ({ 
  fees, setFees, vatEnabled, setVatEnabled, vatPct, setVatPct, displayDays, displayHours,
  totals
}) => {
  const money = (v: number) => `£${v.toFixed(2)}`;
  const r2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;
  const subtotal = r2(totals.preProfit + totals.profit);
  const totalVat = vatEnabled ? r2(subtotal * vatPct) : 0;
  const goodsSubtotal = r2(totals.materialsTotal + totals.equipmentTotal + totals.otherTotal);
  const labourSubtotal = r2(totals.labourTotal);
  const goodsIncVat = vatEnabled ? r2(goodsSubtotal * (1 + vatPct)) : goodsSubtotal;
  const labourIncVat = vatEnabled ? r2(labourSubtotal * (1 + vatPct)) : labourSubtotal;
  const baseProfitOnly = r2(totals.baseCost * (fees.profitPct ?? 0));
  const profitDisplay = r2(totals.adminFee + totals.contingency + baseProfitOnly);
  const hasSpecifiedDuration = Boolean(
    (displayDays != null && displayDays > 0) || (displayHours != null && displayHours > 0)
  );
  const effectiveDays = displayDays && displayDays > 0
    ? Math.ceil(displayDays)
    : 1;
  const profitPerDay = hasSpecifiedDuration ? r2(profitDisplay / effectiveDays) : null;
  const profitPerDayLabel = `Profit Per Day (${effectiveDays} day${effectiveDays === 1 ? '' : 's'})`;

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Typography level="title-md" sx={{ mb: 1 }}>Totals</Typography>
      <Stack spacing={0.5} sx={{ mb: 1 }}>
        <Row label="Materials Total (excl. VAT)" value={money(totals.materialsTotal)} />
        <Row label="Equipment Total (excl. VAT)" value={money(totals.equipmentTotal)} />
        <Row label="Additional Costs Total (excl. VAT)" value={money(totals.otherTotal)} />
        <Row label="Labour Total (excl. VAT)" value={money(totals.labourTotal)} />
      </Stack>
      <Divider />
      <Typography level="title-md" sx={{ mb: 1 }}>Company Additions</Typography>
      
      {/* Admin Fee Slider */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ my: 0.5 }}>
        <Typography level="body-sm">Admin fee (max £{fees.adminCap}) ({Math.round(fees.adminPct * 100)}%)</Typography>
        <Input
          type="number"
          size="sm"
          value={totals.adminFee.toFixed(2)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const typedValue = Number(e.target.value || 0);
            if (totals.baseCost > 0) {
              const safeValue = isFinite(typedValue) ? Math.max(typedValue, 0) : 0;
              const newPct = safeValue / totals.baseCost;
              // Only adjust the percentage here; keep the cap as a separate, fixed guardrail
              setFees({
                ...fees,
                adminPct: Math.min(Math.max(newPct, 0), 1),
              });
            }
          }}
          startDecorator="£"
          sx={{ width: 120 }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Slider min={0} max={1} step={0.05} value={fees.adminPct} onChange={(_, v) => setFees({ ...fees, adminPct: Number(v) })} valueLabelFormat={(v) => `${Math.round((Number(v) || 0) * 100)}%`} valueLabelDisplay="auto" sx={{ mt: 0.5, flex: 1 }} />
        <Button size="sm" variant="outlined" onClick={() => setFees({ ...fees, adminPct: 0.10 })}>Reset 10%</Button>
      </Stack>
      
      {/* Contingency Slider */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ my: 0.5 }}>
        <Typography level="body-sm">Contingency ({Math.round(fees.contingencyPct * 100)}%)</Typography>
        <Input
          type="number"
          size="sm"
          value={totals.contingency.toFixed(2)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const typedValue = Number(e.target.value || 0);
            if (totals.baseCost > 0) {
              const newPct = typedValue / totals.baseCost;
              setFees({ ...fees, contingencyPct: Math.min(Math.max(newPct, 0), 1) });
            }
          }}
          startDecorator="£"
          sx={{ width: 120 }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Slider min={0} max={1} step={0.05} value={fees.contingencyPct} onChange={(_, v) => setFees({ ...fees, contingencyPct: Number(v) })} valueLabelFormat={(v) => `${Math.round((Number(v) || 0) * 100)}%`} valueLabelDisplay="auto" sx={{ mt: 0.5, flex: 1 }} />
        <Button size="sm" variant="outlined" onClick={() => setFees({ ...fees, contingencyPct: 0.10 })}>Reset 10%</Button>
      </Stack>
      
      {/* Profit Margin Slider */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ my: 0.5 }}>
        <Typography level="body-sm">Profit margin ({Math.round(fees.profitPct * 100)}%)</Typography>
        <Input
          type="number"
          size="sm"
          value={totals.profit.toFixed(2)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const typedValue = Number(e.target.value || 0);
            if (totals.baseCost > 0) {
              const newPct = typedValue / totals.baseCost;
              setFees({ ...fees, profitPct: Math.min(Math.max(newPct, 0), 1) });
            }
          }}
          startDecorator="£"
          sx={{ width: 120 }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Slider min={0} max={1} step={0.05} value={fees.profitPct} onChange={(_, v) => setFees({ ...fees, profitPct: Number(v) })} valueLabelFormat={(v) => `${Math.round((Number(v) || 0) * 100)}%`} valueLabelDisplay="auto" sx={{ mt: 0.5, flex: 1 }} />
        <Button size="sm" variant="outlined" onClick={() => setFees({ ...fees, profitPct: 0.10 })}>Reset 10%</Button>
      </Stack>
      <Divider sx={{ my: 1 }} />
      <Stack direction="column" spacing={2} sx={{ mb: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography level="body-sm">Charge VAT</Typography>
            <Switch checked={vatEnabled} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVatEnabled(e.target.checked)} />
          </Stack>
          <Select value={String(vatPct)} onChange={(_, v) => setVatPct(Number(v))} size="sm" disabled={!vatEnabled} sx={{ minWidth: { xs: '100%', sm: 140 } }}>
            <Option value={String(0.2)}>20%</Option>
            <Option value={String(0)}>0% (Zero-rated)</Option>
          </Select>
        </Stack>
      </Stack>
      <Row
        label={(
          <Tooltip
            title="Total of Materials Total (excl. VAT) + Equipment Total (excl. VAT) + Additional Costs Total (excl. VAT) + Labour Total (excl. VAT)"
            placement="top"
          >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <span>Base Cost Total (pre VAT)</span>
              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  border: '1px solid',
                  borderColor: 'neutral.outlinedBorder',
                  color: 'text.tertiary'
                }}
              >
                ?
              </Box>
            </Box>
          </Tooltip>
        )}
        value={money(totals.baseCost)}
      />
      <Row label="Base + Company Additions (pre VAT)" value={money(subtotal)} />
      {vatEnabled && (
        <>
          <Row
            label={(
              <Tooltip title="Inclusive of Materials, Equipment & Additional Costs" placement="top">
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <span>Materials (incl. VAT)</span>
                  <Box
                    component="span"
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      border: '1px solid',
                      borderColor: 'neutral.outlinedBorder',
                      color: 'text.tertiary'
                    }}
                  >
                    ?
                  </Box>
                </Box>
              </Tooltip>
            )}
            value={money(goodsIncVat)}
          />
          <Row label="Labour (incl. VAT)" value={money(labourIncVat)} />
          <Row label={`VAT (${Math.round(vatPct*100)}%)`} value={money(totalVat)} bold />
        </>
      )}
      {vatEnabled && (
        <Row label="Total (excl. VAT):" value={money(subtotal)} bold />
      )}
      <Row label={`Total${vatEnabled ? ' (incl. VAT)' : ''}:`} value={money(totals.grandTotal)} bold large />
      <Row label="Profit (- all overheads)" value={money(profitDisplay)} />
      <Row label={profitPerDayLabel} value={profitPerDay == null ? 'please populate project duration' : money(profitPerDay)} />
    </Card>
  );
};
